import { type ReactElement, useEffect, useState } from 'react';
import { type OpsDashboardView, type OpsRideRow } from '@vello/api-client';
import { formatMoney } from '@vello/format';
import { dashboardKpiCards, formatMarketInstant, loadDashboard, marketDisplayName, marketTimeZone, platformCostsAuthority } from '../../src/index';
import { NowPanel } from '../components/now-panel';
import { PeriodDashboard } from '../components/period-dashboard';
import { PlatformCostsBlock } from '../components/platform-costs';
import { IdRef } from '../components/code-ref';
import { useAdmin } from '../session';
import { useRouter } from '../router';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { Badge, EmptyState, ListTable, PageHeader, StatCard, SectionTitle, Grid } from '@vello/web-theme';

/**
 * Operations dashboard (S112, wired; D358 honest numbers): scoped KPIs + the live active-ride queue read from the real
 * server ops-read feed (`GET /v1/ops/dashboard`), scope-contained by the server. A KPI the read model cannot compute
 * (the server names it in `unavailable`, or a rate over zero rides) reads "unavailable" — never a placeholder 0 or a
 * test currency. Money is formatted exactly from integer minor units (`@vello/format`); ride times render in each
 * ride's market timezone; a ride opens the support ride page.
 *
 * D874: the indicators are grouped in labelled blocks - Operação (the ride counts, today so far, the active rides), Financeiro (GMV, take rate) and
 * Custos de plataforma (the Mapas and IA cards: what the platform pays vendors this month, with the forecast) - then the period panel with its own title.
 */
const OPERATION_KPIS = ['requested', 'completed', 'cancelled', 'completionRate'] as const;
const FINANCE_KPIS = ['gmv', 'takeRate'] as const;

export function DashboardScreen(): ReactElement {
  const { runtime, scope, markets, grants } = useAdmin();
  const { mayViewMaps, mayViewAi } = platformCostsAuthority(grants);
  const { navigate } = useRouter();
  const { t, lang } = useT();
  const { fail, v } = useVocab();
  const [view, setView] = useState<OpsDashboardView | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(undefined);
    void loadDashboard(runtime, scope.current, markets).then((res) => {
      if (cancelled) return;
      if (res.ok) setView(res.value);
      else {
        setView(undefined);
        setError(t('dash.error', { code: fail(res.error) }));
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [runtime, scope, markets, t, attempt]);

  const cards = dashboardKpiCards(view);
  const value = (card: (typeof cards)[number]): string => {
    switch (card.kind) {
      case 'loading':
        return '—';
      case 'unavailable':
        return t('ui.unavailable');
      case 'count':
        return new Intl.NumberFormat(lang).format(card.value);
      case 'ratio':
        return new Intl.NumberFormat(lang, { style: 'percent', maximumFractionDigits: 1 }).format(card.value);
      case 'money':
        return formatMoney({ amountMinor: card.amountMinor, currency: card.currency }, { locale: lang, timeZone: 'UTC', measurement: 'metric' });
    }
  };
  // D513: the numbers count up on first load (and tween old → new if the view ever refreshes). Money counts only while the
  // minor-unit amount is a safe integer, so the number shown is always exact; a bigger one is shown as the exact string.
  const countFor = (card: (typeof cards)[number]): { value: number; format: (n: number) => string; decimals?: number } | undefined => {
    switch (card.kind) {
      case 'count':
        return { value: card.value, format: (n) => new Intl.NumberFormat(lang).format(n) };
      case 'ratio':
        return { value: card.value, format: (n) => new Intl.NumberFormat(lang, { style: 'percent', maximumFractionDigits: 1 }).format(n), decimals: 4 };
      case 'money': {
        const n = Number(card.amountMinor);
        return Number.isSafeInteger(n) ? { value: n, format: (x) => formatMoney({ amountMinor: BigInt(Math.round(x)), currency: card.currency }, { locale: lang, timeZone: 'UTC', measurement: 'metric' }) } : undefined;
      }
      default:
        return undefined;
    }
  };
  const hintFor = (card: (typeof cards)[number]): string | undefined =>
    card.kind === 'unavailable' ? t('dash.kpi.unavailable.hint') : card.id === 'takeRate' ? t('dash.kpi.takeRate.hint') : undefined;

  const renderCard = (id: string): ReactElement | null => {
    const card = cards.find((c) => c.id === id);
    if (card === undefined) return null;
    const hint = hintFor(card);
    const count = countFor(card);
    return <StatCard key={card.id} label={t(`dash.kpi.${card.id}`)} value={value(card)} loading={card.kind === 'loading' && error === undefined} loadingLabel={t('ui.loading')} {...(count !== undefined ? { count } : {})} {...(hint !== undefined ? { hint } : {})} />;
  };

  return (
    <>
      <PageHeader title={t('dash.title')} subtitle={t('dash.subtitle')} />

      {/* ---- Operação ---- */}
      <SectionTitle>{t('dash.block.operation')}</SectionTitle>
      <Grid min="xs" gap={4}>{OPERATION_KPIS.map(renderCard)}</Grid>

      {/* D559 (A02-HOURLY): today so far, hour by hour, on the market's own clock. */}
      <NowPanel />

      <SectionTitle level={3} size="sub">{t('dash.rides.title')}</SectionTitle>
      <ListTable
        columns={[
          { key: 'ride', header: t('dash.col.ride'), width: '1.2fr', render: (r: OpsRideRow) => <IdRef value={r.rideId} /> },
          { key: 'status', header: t('dash.col.status'), width: '1fr', render: (r: OpsRideRow) => <Badge tone="neutral">{v('ride.status', r.status)}</Badge> },
          ...(scope.current.city === undefined ? [{ key: 'city', header: t('dash.col.city'), width: '1.2fr', render: (r: OpsRideRow) => marketDisplayName(r.city, markets) }] : []),
          { key: 'requested', header: t('ride.col.requestedAt'), width: '1.2fr', render: (r: OpsRideRow) => <span className="tnum">{formatMarketInstant(r.createdAt, marketTimeZone(r.city, markets), lang)}</span> },
          { key: 'category', header: t('dash.col.category'), width: '.8fr', render: (r: OpsRideRow) => v('ride.category', r.categoryCode) },
        ]}
        rows={view?.rides ?? []}
        rowKey={(r) => r.rideId}
        onRowClick={(r) => navigate('ride_detail', { id: r.rideId })}
        loading={loading}
        loadingLabel={t('ui.loading')}
        error={error}
        onRetry={() => setAttempt((a) => a + 1)}
        retryLabel={t('ui.retry')}
        empty={<EmptyState title={t('dash.empty.rides')} />}
      />

      {/* ---- Financeiro ---- */}
      <SectionTitle>{t('dash.block.finance')}</SectionTitle>
      <Grid min="xs" gap={4}>{FINANCE_KPIS.map(renderCard)}</Grid>

      {/* ---- Custos de plataforma (D874): maps and AI, month to date, with the forecast ---- */}
      {(mayViewMaps || mayViewAi) && (
        <>
          <SectionTitle>{t('dash.block.costs')}</SectionTitle>
          <PlatformCostsBlock />
        </>
      )}

      {/* D471 (C-031, A-006): the period, the supply, the no-driver rate and the revenue per currency. */}
      <PeriodDashboard />
    </>
  );
}
